import { Check } from '@gravity-ui/icons';
import {
  Autocomplete,
  Drawer,
  ListBox,
  ListLayout,
  Popover,
  SearchField,
  Virtualizer,
} from '@heroui/react';
import { DynamicFlag, FlagUtils } from '@sankyu/react-circle-flags';
import type { Nullable, Optional } from '@silverhand/essentials';
import classNames from 'classnames';
import type { ReactNode, RefObject } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import usePlatform from '@/hooks/use-platform';
import useScrimThemeColor from '@/hooks/use-scrim-theme-color';
import type { CountryMetaData } from '@/utils/country-code';

type Props = {
  readonly isOpen: boolean;
  readonly countryCode: string;
  readonly countryList: CountryMetaData[];
  readonly inputRef?: RefObject<Optional<Nullable<HTMLInputElement>>>;
  /** The element the desktop popover aligns to; defaults to the input's field group. */
  readonly anchorRef?: RefObject<Nullable<HTMLElement>>;
  readonly onClose: () => void;
  readonly onChange?: (value: string) => void;
  /** The control that opens the picker; wired as the desktop popover's trigger. */
  readonly children?: ReactNode;
};

/**
 * Ranked so the most relevant match is first (and thus the one Enter commits):
 * exact name > name prefix > calling-code prefix > name substring.
 */
const rankCountries = (countryList: CountryMetaData[], search: string) => {
  const query = search.trim().toLowerCase();

  if (!query) {
    return countryList;
  }

  const numericQuery = query.startsWith('+') ? query.slice(1) : query;

  const score = ({ countryName, countryCallingCode }: CountryMetaData): number => {
    const name = countryName.toLowerCase();

    if (name === query) {
      return 0;
    }
    if (name.startsWith(query)) {
      return 1;
    }
    if (countryCallingCode.startsWith(numericQuery)) {
      return 2;
    }
    if (name.includes(query)) {
      return 3;
    }
    return Number.POSITIVE_INFINITY;
  };

  return countryList
    .map((country) => ({ country, rank: score(country) }))
    .filter(({ rank }) => Number.isFinite(rank))
    .slice()
    .sort((previous, next) => previous.rank - next.rank)
    .map(({ country }) => country);
};

const CountryCodeDropdown = ({
  isOpen,
  countryCode,
  countryList,
  inputRef,
  anchorRef,
  onClose,
  onChange,
  children,
}: Props) => {
  const { isMobile } = usePlatform();
  useScrimThemeColor(isMobile && isOpen);
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  // On mobile the search starts with inputMode="none" so the sheet opens with the list
  // visible instead of under the keyboard; tapping the search field brings the keyboard up.
  const [isSearchEngaged, setIsSearchEngaged] = useState(false);

  const fallbackAnchorRef = useMemo<RefObject<Nullable<HTMLElement>>>(
    () => ({
      get current() {
        return inputRef?.current?.parentElement ?? null;
      },
    }),
    [inputRef]
  );

  const filteredCountryList = useMemo(
    () => rankCountries(countryList, searchValue),
    [countryList, searchValue]
  );

  const close = useCallback(() => {
    setSearchValue('');
    setIsSearchEngaged(false);
    onClose();
    // The popover is anchored to the field group rather than a focusable trigger, so React Aria
    // has nowhere to restore focus and drops it on <body>. The number input is where the user
    // continues anyway.
    requestAnimationFrame(() => {
      inputRef?.current?.focus();
    });
  }, [inputRef, onClose]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        close();
      }
    },
    [close]
  );

  const commit = useCallback(
    (isoCode: string) => {
      const selected = countryList.find(({ countryCode }) => countryCode === isoCode);

      if (selected) {
        onChange?.(selected.countryCallingCode);
      }

      close();
    },
    [close, countryList, onChange]
  );

  const searchField = (
    <SearchField
      fullWidth
      autoFocus={!isMobile}
      name="country-code-search"
      aria-label={t('input.search_region_code')}
    >
      <SearchField.Group>
        <SearchField.Input
          placeholder={t('input.search_region_code')}
          inputMode={isMobile && !isSearchEngaged ? 'none' : 'search'}
          onPointerDown={
            isMobile
              ? () => {
                  setIsSearchEngaged(true);
                }
              : undefined
          }
        />
        <SearchField.ClearButton />
      </SearchField.Group>
    </SearchField>
  );

  // ~220 rows, each with an SVG flag: rendering them all made the picker take ~500ms to open.
  const countryListBox = (
    <Virtualizer layout={ListLayout} layoutOptions={{ rowHeight: 36, gap: 4 }}>
      <ListBox
        aria-label={t('input.search_region_code')}
        items={filteredCountryList}
        selectionMode="none"
        className={classNames(
          'overflow-y-auto overscroll-contain p-0',
          isMobile ? 'h-[min(24rem,55svh)]' : 'max-h-80'
        )}
        renderEmptyState={() => (
          <div className="px-2 py-6 text-center text-sm text-muted">
            {t('description.no_region_code_found')}
          </div>
        )}
        onAction={(key) => {
          commit(String(key));
        }}
      >
        {({ countryCallingCode, countryCode: isoCode, countryName }) => (
          <ListBox.Item
            id={isoCode}
            textValue={countryName}
            data-id={isoCode}
            // Focus here is virtual (the caret stays in the search), so the active row is a fill
            // like a menu's; HeroUI's outset ring was also clipped by the scrolling list.
            className="data-[focus-visible=true]:bg-default data-[focus-visible=true]:ring-0 data-[focus-visible=true]:ring-offset-0"
          >
            {FlagUtils.isValidCountryCode(isoCode) && (
              <DynamicFlag
                code={isoCode.toLowerCase()}
                width={24}
                height={24}
                aria-hidden="true"
                className="size-6 shrink-0 rounded-full"
              />
            )}
            <span className="min-w-0 flex-1 truncate text-foreground" title={countryName}>
              {countryName}
            </span>
            <span dir="ltr" className="shrink-0 text-muted tabular-nums">
              {`+${countryCallingCode}`}
            </span>
            {countryCallingCode === countryCode && (
              <Check aria-hidden className="size-4 shrink-0 text-accent" />
            )}
          </ListBox.Item>
        )}
      </ListBox>
    </Virtualizer>
  );

  if (isMobile) {
    return (
      <>
        {children}
        <Drawer.Backdrop isOpen={isOpen} onOpenChange={onOpenChange}>
          <Drawer.Content placement="bottom">
            <Drawer.Dialog aria-label={t('input.search_region_code')} className="max-h-[85%]">
              <Drawer.Handle />
              <Autocomplete.Filter inputValue={searchValue} onInputChange={setSearchValue}>
                <Drawer.Header className="flex flex-col gap-3">
                  {/* Named for screen readers; visually the search placeholder already says it. */}
                  <Drawer.Heading className="sr-only">
                    {t('input.search_region_code')}
                  </Drawer.Heading>
                  {searchField}
                </Drawer.Header>
                <Drawer.Body className="overscroll-contain pb-[max(env(safe-area-inset-bottom),12px)]">
                  {countryListBox}
                </Drawer.Body>
              </Autocomplete.Filter>
            </Drawer.Dialog>
          </Drawer.Content>
        </Drawer.Backdrop>
      </>
    );
  }

  return (
    <Popover isOpen={isOpen} onOpenChange={onOpenChange}>
      {children}
      <Popover.Content
        triggerRef={anchorRef ?? fallbackAnchorRef}
        placement="bottom start"
        className="w-(--trigger-width) min-w-72"
      >
        <Popover.Dialog
          aria-label={t('input.search_region_code')}
          className="flex flex-col gap-2 p-2"
        >
          <Autocomplete.Filter inputValue={searchValue} onInputChange={setSearchValue}>
            {searchField}
            {countryListBox}
          </Autocomplete.Filter>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
};

export default CountryCodeDropdown;
